import { __awaiter, __decorate, __generator, __metadata, __param } from "tslib";
import { container, delay, inject, singleton } from 'tsyringe';
import { BasketCheckoutComponent, BasketIconComponent, BasketTotalComponent, BasketUIComponent, BasketUIMessagesComponent, } from './components';
import { BasketAuthorizationPlugin, BasketCouponCodePlugin, BasketEmailPlugin, BasketEmptyMessagePlugin, BasketEngravingPlugin, BasketGiftWrappingPlugin, BasketLoaderPlugin, BasketLocationWarningPlugin, BasketShippingMessagePlugin, } from './plugins';
import { BasketExceptionHandlerPlugin, BasketItemExceptionHandlerPlugin } from './plugins/error-handlers';
import { BasketEventBus, BasketStore, SFCCReferralService } from './services';
import './basket.scss';
/**
 * Singleton
 */
var Basket = /** @class */ (function () {
    // Injection of BasketStore and SFCCReferralService are delayed
    // to avoid tsyringe circular dependency exception
    function Basket(eventBus, basketStore, sfccReferralService, settings, storage, translations) {
        this.eventBus = eventBus;
        this.basketStore = basketStore;
        this.sfccReferralService = sfccReferralService;
        this.settings = settings;
        this.storage = storage;
        this.translations = translations;
        this.plugins = [];
    }
    Basket.prototype.bootstrap = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.sfccReferralService.saveParametersIfAvailable();
                        this.createEventBus();
                        this.registerErrorHandlers();
                        return [4 /*yield*/, this.createBasketStore()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.createUI()];
                    case 2:
                        _a.sent();
                        this.registerPlugins();
                        return [2 /*return*/];
                }
            });
        });
    };
    Basket.prototype.createEventBus = function () {
        var _this = this;
        // Basket products
        this.eventBus.subscribe("add-to-basket" /* BasketEvent.AddToBasket */, function (sku, services, engravingMessage) { return __awaiter(_this, void 0, void 0, function () {
            var skusToSend;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        skusToSend = Array.isArray(sku) ? sku : [sku];
                        return [4 /*yield*/, this.handleBasketAction(this.basketStore.addProducts(skusToSend, services, engravingMessage), function () { _this.eventBus.post("show-basket-ui" /* BasketEvent.ShowBasketUI */); })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); });
        this.eventBus.subscribe("set-quantity" /* BasketEvent.SetQuantity */, function (itemId, sku, quantity) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.handleBasketAction(this.basketStore.setQuantity(itemId, sku, quantity))];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); });
        this.eventBus.subscribe("remove-from-basket" /* BasketEvent.RemoveFromBasket */, function (itemId) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.handleBasketAction(this.basketStore.removeProduct(itemId))];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); });
        // Basket coupon codes
        this.eventBus.subscribe("apply-coupon" /* BasketEvent.ApplyCoupon */, function (couponCode) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.handleBasketAction(this.basketStore.applyCoupon(couponCode))];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); });
        this.eventBus.subscribe("remove-coupon" /* BasketEvent.RemoveCoupon */, function (couponItemId) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.handleBasketAction(this.basketStore.removeCoupon(couponItemId))];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); });
        // Basket service
        this.eventBus.subscribe("set-service-value" /* BasketEvent.SetServiceValue */, function (itemId, quantity, serviceSku, serviceValue, engravingMessage) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.handleBasketAction(this.basketStore.setServiceValue(itemId, quantity, serviceSku, serviceValue, engravingMessage))];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); });
        // Basket plugins
        this.eventBus.subscribe("register-plugin" /* BasketEvent.RegisterPlugin */, function (ctor) { return __awaiter(_this, void 0, void 0, function () {
            var plugin;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        plugin = container.resolve(ctor);
                        return [4 /*yield*/, plugin.shouldRegister()];
                    case 1:
                        if (!(_b.sent())) {
                            return [2 /*return*/];
                        }
                        this.plugins.push(plugin);
                        return [4 /*yield*/, plugin.register({
                                eventBus: this.eventBus,
                                settings: this.settings,
                                state: (_a = this.basketStore) === null || _a === void 0 ? void 0 : _a.state,
                                storage: this.storage,
                                translations: this.translations,
                            })];
                    case 2:
                        _b.sent();
                        return [2 /*return*/];
                }
            });
        }); });
        this.eventBus.subscribe("unregister-plugin" /* BasketEvent.UnregisterPlugin */, function (ctor) {
            var pluginIndex = _this.plugins.findIndex(function (x) { return (x instanceof ctor); });
            var plugin = _this.plugins[pluginIndex];
            if (!!plugin) {
                _this.plugins.splice(pluginIndex, 1);
                plugin.unregister();
            }
        });
        // Share the event bus with others.
        window.BasketEventBus = this.eventBus;
        window.dispatchEvent(new Event("event-bus-ready" /* BasketEvent.EventBusReady */));
    };
    Basket.prototype.registerErrorHandlers = function () {
        container.resolve(BasketUIMessagesComponent).register();
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketExceptionHandlerPlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketItemExceptionHandlerPlugin);
    };
    Basket.prototype.createBasketStore = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.handleBasketAction(this.basketStore.restore())];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    Basket.prototype.createUI = function () {
        return __awaiter(this, void 0, void 0, function () {
            var state;
            return __generator(this, function (_a) {
                state = this.basketStore.state;
                container.resolve(BasketIconComponent).register(state);
                container.resolve(BasketUIComponent).register(state);
                container.resolve(BasketTotalComponent).register(state);
                container.resolve(BasketCheckoutComponent).register(state);
                this.eventBus.post("basket-ui-dom-ready" /* BasketEvent.BasketUIDomReady */);
                return [2 /*return*/];
            });
        });
    };
    Basket.prototype.registerPlugins = function () {
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketEmailPlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketEmptyMessagePlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketLoaderPlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketShippingMessagePlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketLocationWarningPlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketCouponCodePlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketEngravingPlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketGiftWrappingPlugin);
        this.eventBus.post("register-plugin" /* BasketEvent.RegisterPlugin */, BasketAuthorizationPlugin);
    };
    // tslint:disable-next-line: no-empty
    Basket.prototype.handleBasketAction = function (basketAction, success) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, basketAction
                            .then(function () {
                            _this.eventBus.post("basket-updated" /* BasketEvent.BasketUpdated */, _this.basketStore.state);
                            if (success) {
                                success();
                            }
                        })
                            .catch(function (response) {
                            _this.eventBus.post("error-occurred" /* BasketEvent.ErrorOccurred */, response.fault);
                        })];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    Basket = __decorate([
        singleton(),
        __param(1, inject(delay(function () { return BasketStore; }))),
        __param(2, inject(delay(function () { return SFCCReferralService; }))),
        __param(3, inject("basketSettings" /* DiToken.Settings */)),
        __param(4, inject("basketStorage" /* DiToken.Storage */)),
        __param(5, inject("basketTranslations" /* DiToken.Translations */)),
        __metadata("design:paramtypes", [BasketEventBus,
            BasketStore,
            SFCCReferralService, Object, Storage, Object])
    ], Basket);
    return Basket;
}());
export { Basket };
